import { useMemo, useState } from 'react';

import { fmt } from '@dcc-erp/shared';
import type { PettyCashFloatListRow } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { todayIso } from './pc-helpers';
import { useCoa } from '../../lib/queries/coa';
import { useCreatePettyCashReplenishment } from '../../lib/queries/petty-cash';
import { SearchSelect } from '../SearchSelect';

// ============================================================
// Replenish modal — pulls all POSTED entries for the picked float, sums them,
// and posts a refill voucher (Dr 1115 / Cr {bank}). The selective-entryIds
// flow is supported by the API but not yet exposed in the UI — Phase-3c.
// ============================================================

interface PCReplenModalProps {
  companyId: string | undefined;
  liveFloats: readonly PettyCashFloatListRow[];
  initialFloatId?: string | undefined;
  onClose: () => void;
}

export function PCReplenModal({
  companyId,
  liveFloats,
  initialFloatId,
  onClose,
}: PCReplenModalProps) {
  const activeFloats = useMemo(() => liveFloats.filter((f) => f.status === 'ACTIVE'), [liveFloats]);
  const [floatId, setFloatId] = useState<string>(initialFloatId ?? activeFloats[0]?.id ?? '');
  const [replenishDate, setReplenishDate] = useState<string>(todayIso());
  const { data: coa = [] } = useCoa(companyId, true);
  const bankAccounts = useMemo(
    // 8-digit COA: cash & bank deposits live under the 101xxxxx group; exclude
    // control rows. (The legacy 1110-1119 4-digit filter no longer matches.)
    () =>
      coa.filter(
        (a) => a.type === 'ASSET' && a.isActive && !a.isControl && a.code.startsWith('101'),
      ),
    [coa],
  );
  // default to the first bank-deposit account (1012…) once COA loads; fall
  // back to the first 101xxxxx account if no 1012 prefix exists.
  const defaultBankCode = useMemo(
    () => (bankAccounts.find((a) => a.code.startsWith('1012')) ?? bankAccounts[0])?.code ?? '',
    [bankAccounts],
  );
  const [fromAccountCode, setFromAccountCode] = useState<string>('');
  // Resolve the effective value: user selection takes priority, otherwise the
  // derived default (avoids setState-in-effect pattern).
  const resolvedFromAccount = fromAccountCode || defaultBankCode;
  const { mutate: createReplen, isPending, error } = useCreatePettyCashReplenishment(companyId);

  const selected = activeFloats.find((f) => f.id === floatId);
  const replenishAmount = selected
    ? Number(selected.floatAmount) - Number(selected.currentBalance)
    : 0;

  function handleSubmit() {
    if (!floatId || !resolvedFromAccount) return;
    createReplen(
      { data: { floatId, replenishDate, fromAccountCode: resolvedFromAccount } },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 560,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              เติมเงินสดย่อย (Replenishment)
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              จะรวมรายการ POSTED ทั้งหมดของ float ที่เลือก แล้วโอนคืนจากธนาคาร
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              Float สาขา
            </label>
            <select
              className="select"
              value={floatId}
              onChange={(e) => setFloatId(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
              disabled={activeFloats.length === 0}
            >
              {activeFloats.length === 0 && <option value="">— ไม่มี float ที่ active —</option>}
              {activeFloats.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.branchName} · วงเงิน {fmt(Number(f.floatAmount))} / คงเหลือ{' '}
                  {fmt(Number(f.currentBalance))}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                วันที่เติมเงิน
              </label>
              <input
                className="input"
                type="date"
                value={replenishDate}
                onChange={(e) => setReplenishDate(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                บัญชีต้นทาง (COA)
              </label>
              <SearchSelect
                value={resolvedFromAccount}
                onChange={setFromAccountCode}
                placeholder="— เลือกบัญชีธนาคาร —"
                options={bankAccounts.map((a) => ({
                  value: a.code,
                  label: `${a.code} — ${a.nameTh}`,
                }))}
                getLabel={(v) => {
                  const a = bankAccounts.find((x) => x.code === v);
                  return a ? `${a.code} — ${a.nameTh}` : '';
                }}
              />
            </div>
          </div>

          <div
            style={{
              padding: '12px 14px',
              background: '#fafafa',
              borderRadius: 8,
              border: '1px solid var(--border)',
              fontSize: 14,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span className="muted">ยอดที่จะเติม (ประมาณ จาก float - currentBalance)</span>
            <span style={{ fontWeight: 600, fontFamily: 'var(--font-display)' }} className="tnum">
              {fmt(replenishAmount)} บาท
            </span>
          </div>

          {error && (
            <div
              style={{
                padding: '10px 12px',
                background: 'var(--negative-bg)',
                borderRadius: 8,
                border: '1px solid var(--negative)',
                color: 'var(--negative)',
                fontSize: 14,
              }}
            >
              {error.message}
            </div>
          )}
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose} disabled={isPending}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            onClick={handleSubmit}
            disabled={isPending || !floatId || !resolvedFromAccount}
          >
            <Icon name="check" size={14} /> {isPending ? 'กำลังเติม…' : 'ยืนยันเติมเงิน'}
          </button>
        </div>
      </div>
    </div>
  );
}

export type { PCReplenModalProps };
